import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface CsvProject {
  id: string;
  name: string;
  description?: string;
  color?: string;
  user_id: string;
  created_at: string;
  updated_at: string;
}

interface CreateProjectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  editingProject?: CsvProject | null;
}

const PROJECT_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Yellow
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#F97316', // Orange
  '#06B6D4', // Cyan
  '#84CC16', // Lime
  '#EC4899', // Pink
  '#6B7280', // Gray
];

export const CreateProjectDialog: React.FC<CreateProjectDialogProps> = ({
  isOpen,
  onClose,
  editingProject
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedColor, setSelectedColor] = useState(PROJECT_COLORS[0]);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Reset form when dialog opens/closes or editing project changes
  useEffect(() => {
    if (isOpen) {
      if (editingProject) {
        setName(editingProject.name);
        setDescription(editingProject.description || '');
        setSelectedColor(editingProject.color || PROJECT_COLORS[0]);
      } else {
        setName('');
        setDescription('');
        setSelectedColor(PROJECT_COLORS[0]);
      }
    }
  }, [isOpen, editingProject]);

  const createProjectMutation = useMutation({
    mutationFn: async (projectData: { name: string; description?: string; color: string }) => {
      if (!user?.id) throw new Error('User not authenticated');

      if (editingProject) {
        // Update existing project
        const { error } = await supabase
          .from('csv_projects')
          .update({
            name: projectData.name,
            description: projectData.description,
            color: projectData.color,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingProject.id)
          .eq('user_id', user.id);
          
        if (error) throw error;
      } else {
        // Create new project
        const { error } = await supabase
          .from('csv_projects')
          .insert({
            user_id: user.id,
            name: projectData.name,
            description: projectData.description,
            color: projectData.color
          });
          
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['csv-projects'] });
      toast.success(editingProject ? 'Project updated successfully' : 'Project created successfully');
      onClose();
    },
    onError: (error) => {
      console.error('Error saving project:', error);
      toast.error(editingProject ? 'Failed to update project' : 'Failed to create project');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('Project name is required');
      return;
    }

    createProjectMutation.mutate({
      name: name.trim(),
      description: description.trim() || undefined,
      color: selectedColor
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {editingProject ? 'Edit Project' : 'Create New Project'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Project Name *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., EUR/USD Strategy, Q1 2024 Campaign"
              maxLength={100}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description for this project..."
              rows={3}
              maxLength={500}
            />
          </div>

          <div className="space-y-2">
            <Label>Color</Label>
            <div className="flex gap-2 flex-wrap">
              {PROJECT_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    selectedColor === color 
                      ? 'border-primary scale-110' 
                      : 'border-gray-300 hover:scale-105'
                  }`}
                  style={{ backgroundColor: color }}
                  onClick={() => setSelectedColor(color)}
                />
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={createProjectMutation.isPending || !name.trim()}
            >
              {createProjectMutation.isPending 
                ? (editingProject ? 'Updating...' : 'Creating...') 
                : (editingProject ? 'Update Project' : 'Create Project')
              }
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};